import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { verifyToken } from "shared/api/client/orvalHelpers";
import {
  normalizeUserRole,
  type AppUserRole,
} from "shared/auth/constants/roles";

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: readonly AppUserRole[];
  redirectTo?: string;
  redirectByRole?: Partial<Record<AppUserRole, string>>;
  unauthorizedFallback?: React.ReactNode;
}

const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles = ["USER", "CREATOR", "CURATOR", "ADMIN"],
  redirectTo = "/home/pair_headset",
  redirectByRole,
  unauthorizedFallback,
}) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [resolvedRole, setResolvedRole] = useState<AppUserRole | null>(null);
  const [authFailureKind, setAuthFailureKind] = useState<
    "token" | "role" | null
  >(null);

  const clearAuthCookies = () => {
    const names = [
      "userToken",
      "userId",
      "userEmail",
      "userFirstName",
      "userLastName",
      "userIsVerified",
      "userRole",
    ];
    names.forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure`;
    });
  };

  useEffect(() => {
    const checkAuthorization = async () => {
      const token = getCookie("userToken");
      const userRole = getCookie("userRole");

      if (!token) {
        setAuthFailureKind("token");
        setIsAuthorized(false);
        return;
      }

      try {
        const isTokenValid = await verifyToken(token);
        if (!isTokenValid) {
          setAuthFailureKind("token");
          setIsAuthorized(false);
          return;
        }
      } catch {
        setAuthFailureKind("token");
        setIsAuthorized(false);
        return;
      }

      const role = normalizeUserRole(userRole);
      if (!role) {
        setAuthFailureKind("token");
        setIsAuthorized(false);
        return;
      }

      setResolvedRole(role);
      const ok = allowedRoles.includes(role);
      setAuthFailureKind(ok ? null : "role");
      setIsAuthorized(ok);
    };

    checkAuthorization();
  }, [allowedRoles]);

  if (isAuthorized === null) {
    return (
      <div className="library-creator-content">
        <div className="main-library">
          <div className="top-container-library">
            <div className="left-top-container-library">
              <div className="title-top-container-library">Library</div>
              <div className="subtitle-top-container-library">
                Manage your uploaded titles
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    if (authFailureKind === "token") {
      clearAuthCookies();
    }
    if (unauthorizedFallback) {
      return <>{unauthorizedFallback}</>;
    }
    const target =
      (resolvedRole && redirectByRole?.[resolvedRole]) ?? redirectTo;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;
